import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { Button, ElasticFormModal } from 'basic-components';
import { DataLakeQuery } from 'dashboard-services/api/dataLake';
import Query from 'dashboard-services/api/Query';
import { CommonService, ReduxService } from 'dashboard-services/services';
import { CodeMirrorWrapper, globalActions, spreadsheetOptions, statusBoxActions } from 'primary-components';

import { MAX_CSV_SIZE_MB } from 'actions/modules/datalake/common';
import { csvEditorActions } from 'actions/modules/tools';
import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';
import { sendEvent } from 'services/GAService';

import FileDetails from 'basic/FileDetails';

import Worksheet from './Worksheet';

import { useQueryClient } from '@tanstack/react-query';
import Papa from 'papaparse';
import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';
import './CSVEditor.scss';

const MAX_CSV_SIZE_BYTES = MAX_CSV_SIZE_MB * 1000 * 1000
const ctrlKey = 17, rightCmdKey = 93, leftCmdKey = 91, sKey = 83
const ROW_HEIGHT = 32, VIEW_HEIGHT = 0.8
const CSV = "csv"
const TOO_FEW_FIELDS = "TooFewFields"
const CSVEditor = () => {
  const dispatch = useDispatch(),
        ctrlDown = useRef(false),
        { id: fileId, editable, limit, newFile, groupName: groupNameLoc, provider, onSubmitCallback } = useSelector(state => state.toolsState.csvEditorState),
        [display, setDisplay] = useState(CSV),
        [content, setContent] = useState(),
        [fileInfo, setFileInfo] = useState({}),
        [isLarge, setIsLarge] = useState(false),
        [errors, setErrors] = useState([]),
        [areFileDetailsEditable, setAreFileDetailsEditable] = useState(newFile),
        worksheetRef = useRef(),
        formRef = useRef(),
        editorRef = useRef(),
        submitProps = useMemo(() => ({
          disabled: !editable
        }), [editable]),
        changeDisplay = useCallback(() => {
          if (display === CSV) {
            setContent(worksheetRef.current.getAsCSV(content))
            setDisplay("text")
          } else {
            setDisplay(CSV)
            isPending.current = true
            Promise.resolve().then(() => {
              const headers = {}
              Papa.parse(editorRef.current?.doc?.getValue(), {
                skipEmptyLines: true,
                header: true,
                complete: result => {
                  isPending.current = false
                  if(result?.errors.length === 0 || typeof content === "string") {
                    worksheetRef.current.setData(result?.data)
                    worksheetRef.current.setColumns({ columnsArray: Object.values(headers) || [], data: result?.data })
                  } else {
                    dispatch(globalActions.getMessageStore()).error("CSV file can not be parsed")
                    console.error(result?.errors)
                  }
                },
                error: result => {
                  isPending.current = false
                  dispatch(globalActions.getMessageStore()).error("CSV file can not be parsed")
                  console.error(result?.errors)
                },
                transformHeader: (header, index) => {
                  const objectInHeaders = headers[String(index)] 
                  if(objectInHeaders === undefined){
                    const columnId = CommonService.generateUID()
                    headers[String(index)] = { columnId, columnName: header }
                    return columnId
                  }
                  return objectInHeaders.columnId
                }
              })
            })
          }
        }, [display, content, dispatch]),
        close = useCallback(() => {
          batch(() => {
            dispatch(csvEditorActions.changeValue(undefined, 'id'))
            dispatch(csvEditorActions.changeValue(false, 'open'))
            dispatch(csvEditorActions.changeValue(false, 'newFile'))
            dispatch(csvEditorActions.changeValue(false, 'editable'))
            dispatch(csvEditorActions.changeValue(undefined, 'provider'))
            dispatch(csvEditorActions.changeValue(undefined, 'onSubmitCallback'))
          })
        }, [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(async response => {
              const location = response?.headers?.get("Location")
              const parsedData = await response.json()
              return ReduxService.updateObject(parsedData, { location })
            })
            .withHeader("Target", provider, true)
        , [dispatch, provider]),
        getQueryBuilderS3 = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
        , [dispatch]),
        { data: infoResponse } = filesDatalakeQueries.useGetFileDefinition({ getQueryBuilder, id: fileId, enabled: !newFile && !!fileId, refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false }),
        location = useMemo(() => infoResponse?.location, [infoResponse]),
        { mutateAsync: s3GetCall } = filesDatalakeQueries.useGetFileContentMutation({ getQueryBuilder: getQueryBuilderS3, url: location }),
        isPending = useRef(false),
        downloadFile = useCallback(() => {
          const reqId = CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: reqId }))
          isPending.current = true;
          const onErrorCall = () => 
            s3GetCall()
              .then(response => response.text())
              .then(response => {
                setDisplay("text")
                setContent(response)
                isPending.current = false
              })
              .catch(() => {
                isPending.current = false
              })
          const headers = {}
          let fullNumberOfRows = 0
          return Papa.parse(location, {
            download: true,
            chunkSize: MAX_CSV_SIZE_BYTES,
            chunk: results => {
              const numberOfRows = results.data.length
              fullNumberOfRows += numberOfRows;
              if(window.innerHeight * VIEW_HEIGHT/ROW_HEIGHT < fullNumberOfRows) {
                setIsLarge(true)
              }
              let newResult
              if(results?.errors?.length === 1 && results?.errors[0].code === TOO_FEW_FIELDS && results?.errors[0]?.row === numberOfRows - 1){ // while slicing big files (by chunking) - row sliced in the middle, remove it
                newResult = results?.data.slice(0,-1)
                worksheetRef.current.setData(newResult)
                worksheetRef.current.setColumns({ columnsArray: Object.values(headers) || [], data: newResult })
              } else if (results?.errors?.length === 0){
                newResult = results?.data
                worksheetRef.current.setData(newResult)
                worksheetRef.current.setColumns({ columnsArray: Object.values(headers) || [], data: newResult })
              } else {
                dispatch(globalActions.getMessageStore()).error("CSV file can not be parsed. Raw file will be displayed.")
                console.error(results?.errors)
                dispatch(globalActions.handleRequest({ id: reqId, isResponse: true }))
                isPending.current = false
                return onErrorCall()
              }
              dispatch(globalActions.handleRequest({ id: reqId, isResponse: true }))
              isPending.current = false
            },
            error: error => {
              dispatch(globalActions.getMessageStore()).error("CSV file can not be parsed. Raw file will be displayed.")
              console.error(error)
              dispatch(globalActions.handleRequest({ id: reqId, isResponse: true }))
              isPending.current = false
              return onErrorCall()
            },
            skipEmptyLines: true,
            header: true,
            transformHeader: (header, index) => {
              const objectInHeaders = headers[String(index)] 
              if(objectInHeaders === undefined){
                const columnId = CommonService.generateUID()
                headers[String(index)] = { columnId, columnName: header }
                return columnId
              }
              return objectInHeaders.columnId
            }
          })
        }, [dispatch, location, s3GetCall]),
        optionalText = useMemo(() => limit !== undefined || Number.parseFloat((fileInfo?.size || 0)) > MAX_CSV_SIZE_BYTES ? `Preview (up to ${MAX_CSV_SIZE_MB}MB of data)` : undefined, [fileInfo, limit]),
        fileHeader = useMemo(() => (
          <When condition={content !== undefined ||  worksheetRef.current?.getData() !== undefined || newFile}>
            <FileDetails
                areFileDetailsEditable={areFileDetailsEditable}
                className="ng-csv-editor__top"
                editable={editable}
                errors={errors}
                extension={CSV}
                fileInfo={fileInfo}
                groupNameLoc={groupNameLoc}
                newFile={newFile}
                ref={formRef}
                setAreFileDetailsEditable={setAreFileDetailsEditable}
                setFileInfo={setFileInfo}
            />
          </When>
        ), [content, editable, areFileDetailsEditable, errors, fileInfo, groupNameLoc, newFile]),
        queryClient = useQueryClient(),
        fileFields = useMemo(() => Object.entries(fileInfo?.fields || {}).map(([k, v]) => ({ name: k, value: v })), [fileInfo]),
        getQueryBuilderUpload = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
            .withOnError(({ response = {}, message }) => {
              setErrors(response.validationErrors)
              if(message) {
                dispatch(globalActions.getMessageStore()).error(message)
              }
            })
        , [dispatch]),
        timeoutRef = useRef(),
        { uploadFileMetadata, uploadFileContent } = filesDatalakeQueries.useFileUpload({ getQueryBuilder: getQueryBuilderUpload, fileName: fileInfo?.fileName, groupName: fileInfo?.groupName, fileType: fileInfo?.fileType, fields: fileFields }),
        onSubmit = useCallback(() => {
          if(isPending.current) {
            return Promise.resolve();
          }
          if(!editable) {
            return Promise.resolve();
          }
          if(areFileDetailsEditable) {
            formRef.current?.validateForForm()
            if(!formRef.current?.isValid()) {
              return Promise.resolve();
            }
          }
          const reqId = CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: reqId }))
          return uploadFileMetadata()
            .then(response => {
              const jobId = response.headers.get("X-KH-JOB-ID")
              dispatch(statusBoxActions.addJob({ jobId, displayName: fileInfo.fileName, subtitle: fileInfo.groupName }))
              return response
            })
            .then(response => response.json())
            .then(response => uploadFileContent({
                url: response.location,
                image: display === "csv" ? worksheetRef.current.getAsCSV() : editorRef.current?.doc?.getValue()
              })
            )
            .then(() => dispatch(globalActions.getMessageStore()).success(newFile ? "New file has been saved." : "New version of file has been saved."))
            .then(() => {
              close()
              clearTimeout(timeoutRef.current)
              timeoutRef.current = setTimeout(() => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_FILES() }), 3000)
              onSubmitCallback?.({ name: fileInfo?.fileName, groupName: fileInfo?.groupName })
            })
            .finally(() => dispatch(globalActions.handleRequest({ id: reqId, isResponse: true })))
        }, [editable, areFileDetailsEditable, dispatch, uploadFileMetadata, fileInfo.fileName, fileInfo.groupName, uploadFileContent, display, newFile, close, onSubmitCallback, queryClient]),
        onKeyDown = useCallback(e => {
          if(e.keyCode === 13 || e.keyCode === 27) {
            e.preventDefault()
            e.stopPropagation()
          }
          if(e.keyCode === ctrlKey || e.keyCode === leftCmdKey || e.keyCode === rightCmdKey) {
            ctrlDown.current = true
          }
          if(ctrlDown.current && e.keyCode === sKey) {
            e.preventDefault()
            onSubmit()
          }
        }, [onSubmit]),
        onKeyUp = useCallback(e => {
          if(e.keyCode === ctrlKey || e.keyCode === leftCmdKey || e.keyCode === rightCmdKey) {
            ctrlDown.current = false
          }
        }, []),
        clearCtrl = useCallback(() => ctrlDown.current = false, []),
        button = useCallback(() => (
          <Fragment key="fragment">
            <Button
                onClick={changeDisplay}
                size="small"
                type="button"
                variant="secondary"
            >
              {`Display as ${display === "csv" ? "text" : "spreadsheet"}`}
            </Button>
          </Fragment>
        ), [changeDisplay, display])

  useEffect(() => {
    if(!newFile && fileId && location) {
      downloadFile(fileId)
    } else if(newFile) {
      worksheetRef.current?.setEmptyTable?.()
    }
  }, [downloadFile, fileId, location, newFile])

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "csv_editor",
      label: "tool",
    }
    dispatch(sendEvent(event))
  }, [dispatch])

  useEffect(() => {
    content && editorRef.current?.doc?.setValue(content)
  }, [content])

  useEffect(() => {
    if(Object.keys(infoResponse || {}).length > 0) {
      setFileInfo(infoResponse)
    }
  }, [infoResponse])

  useEffect(() => () => {
    clearTimeout(timeoutRef.current)
  }, [])

  return (
    <ElasticFormModal
        buttons={button}
        className={classNames("ng-csv-editor", { "ng-csv-editor--is-large": isLarge })}
        closeOnBackground={false}
        closeOnEsc={false}
        onBlur={clearCtrl}
        onClose={close}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onSubmit={onSubmit}
        optionalText={optionalText}
        submitProps={submitProps}
        tabIndex="0"
        title={fileHeader}
    >
      <If condition={display === "csv"}>
        <Then>
          {() => (
            <Worksheet
                className="ng-csv-editor__content ng-csv-editor__content--csv"
                editable={editable && limit === undefined && Number.parseFloat((fileInfo?.size || 0)) < MAX_CSV_SIZE_BYTES}
                ref={worksheetRef}
            />
          )}
        </Then>
        <Else>
          {() => (
              <CodeMirrorWrapper
                  className="ng-csv-editor__content ng-csv-editor__content--markdown"
                  lineWrapping={false}
                  options={spreadsheetOptions}
                  ref={editorRef}
                  value={content}
              />
          )}
        </Else>
      </If>
    </ElasticFormModal>
  )
}

export default memo(CSVEditor);